/** Var på bilden själva prylen finns – andelar 0–1 av bredd/höjd. */
export type FocusBox = { x: number; y: number; w: number; h: number };

/** Läser fokusrutorna som sparats på annonsen (jsonb) på ett tåligt sätt. */
export function parseFocus(value: unknown): (FocusBox | null)[] {
  if (!Array.isArray(value)) return [];
  return value.map((b) => {
    if (!b || typeof b !== "object") return null;
    const { x, y, w, h } = b as Record<string, unknown>;
    if ([x, y, w, h].some((n) => typeof n !== "number" || !Number.isFinite(n))) return null;
    const box = { x: x as number, y: y as number, w: w as number, h: h as number };
    if (box.w <= 0 || box.h <= 0) return null;
    return box;
  });
}

export const focusCenter = (b: FocusBox) => ({ cx: b.x + b.w / 2, cy: b.y + b.h / 2 });

/**
 * Räknar ut hur bilden ska visas i en ruta med given proportion så att prylen
 * fyller så mycket som möjligt utan att kapas.
 *
 * @param frameAspect  rutans bredd/höjd
 * @param imageAspect  bildens bredd/höjd
 */
export function focusFit(
  box: FocusBox | null | undefined,
  frameAspect: number,
  imageAspect: number,
  /** Hur bilden visas när fokusruta saknas eller inte ryms. */
  fallback: "cover" | "contain" = "contain",
): { objectFit: "cover" | "contain"; objectPosition: string } {
  if (!box || !frameAspect || !imageAspect) {
    return { objectFit: fallback, objectPosition: "50% 50%" };
  }
  const { cx, cy } = focusCenter(box);
  // Vid cover syns bara en del av den ena axeln – hur stor del?
  const visibleX = imageAspect > frameAspect ? frameAspect / imageAspect : 1;
  const visibleY = imageAspect > frameAspect ? 1 : imageAspect / frameAspect;

  // Ryms inte prylen i rutan: fyll ändå rutan om det är önskat läge, men
  // centrera på prylen. Annars visas hela bilden.
  const margin = 1.02;
  if ((box.w * margin > visibleX || box.h * margin > visibleY) && fallback !== "cover") {
    return { objectFit: fallback, objectPosition: "50% 50%" };
  }

  const axis = (center: number, visible: number) => {
    if (visible >= 1) return 50;
    const start = Math.min(Math.max(center - visible / 2, 0), 1 - visible);
    return (start / (1 - visible)) * 100;
  };

  return {
    objectFit: "cover",
    objectPosition: `${axis(cx, visibleX).toFixed(1)}% ${axis(cy, visibleY).toFixed(1)}%`,
  };
}
